import { stopAudioVisualizer } from './audio-visualizer';
import { getLogger } from './logger';
import {
  addStateVisualization,
  createStateIcon,
  removeStateVisualization,
} from './state-visualizations';
import type { WidgetElements, WidgetState } from './types';
import { checkSvgSupport, getTranslations, getWidgetPosition } from './ui-helpers';
import { ensureAnimationStyles } from './ui-styles';
import { createWidgetElements } from './widget-creation';

const logger = getLogger('UI');

/**
 * Global widget instance
 */
let widgetInstance: WidgetElements | null = null;

/**
 * Stop button event handler for proper cleanup and management
 */
let currentStopCallback: (() => void) | null = null;

/**
 * Add immediate visual feedback when stop button is clicked
 * @param stopButton - The stop button element
 */
function addClickVisualFeedback(stopButton: HTMLElement): void {
  logger.debug('Adding click visual feedback to stop button');

  try {
    // Add temporary active state styling
    stopButton.style.transform = 'scale(0.9)';
    stopButton.style.filter = 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3)) brightness(0.8)';
    stopButton.style.transition = 'all 80ms ease-in-out';

    // Add a brief highlight effect
    const originalFilter = stopButton.style.filter;
    stopButton.style.filter = `${originalFilter} brightness(1.2) saturate(1.2)`;

    // Reset after short delay with staggered animation
    setTimeout(() => {
      stopButton.style.filter = `${originalFilter} brightness(1.1)`;
      stopButton.style.transform = 'scale(1.05)';
    }, 100);

    setTimeout(() => {
      stopButton.style.filter = originalFilter;
      stopButton.style.transform = '';
      stopButton.style.transition = 'all 200ms ease-in-out';
    }, 200);

    logger.debug('Click visual feedback applied successfully');
  } catch (error) {
    logger.warn('Error applying click visual feedback', error);
  }
}

/**
 * Setup enhanced stop button event listeners with proper cleanup
 * @param stopButton - The stop button element
 * @param onStopCallback - Callback function for stop events
 */
function setupStopButtonEventListener(stopButton: HTMLElement, onStopCallback: () => void): void {
  logger.debug('Setting up enhanced stop button event listeners');

  // Remove any existing event listeners
  removeStopButtonEventListeners();

  // Store the current callback for cleanup
  currentStopCallback = onStopCallback;

  // Create enhanced event handler with error handling, visual feedback, and accessibility support
  const handleStopClick = (event: Event) => {
    logger.debug('Stop button activated', {
      eventType: event.type,
      target: event.target,
      timestamp: Date.now(),
    });

    try {
      // Prevent default behavior
      event.preventDefault();
      event.stopPropagation();

      // Add immediate visual feedback
      addClickVisualFeedback(stopButton);

      // Execute the callback if it exists
      if (currentStopCallback) {
        logger.debug('Executing stop callback');
        currentStopCallback();
      } else {
        logger.warn('Stop button clicked but no callback is available');
      }
    } catch (error) {
      logger.error('Error executing stop button callback', error);
    }
  };

  // Handle keyboard events for accessibility (Enter and Space keys)
  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      logger.debug('Stop button keyboard activation', { key: event.key });
      event.preventDefault(); // Prevent default browser behavior (scroll for Space)
      handleStopClick(event);
    }
  };

  // Add event listeners with proper options
  stopButton.addEventListener('click', handleStopClick, { passive: false });
  stopButton.addEventListener('keydown', handleKeyDown, { passive: false });

  // Store listeners on the element for cleanup
  (stopButton as any)._sirmaaiStopClickHandler = handleStopClick;
  (stopButton as any)._sirmaaiStopKeyDownHandler = handleKeyDown;

  // Add subtle attention animation to draw user focus
  stopButton.classList.add('attention');

  // Remove attention animation after user interaction or timeout
  const removeAttention = () => {
    stopButton.classList.remove('attention');
    logger.debug('Stop button attention animation removed');
  };

  // Remove attention after 6 seconds or on first interaction
  setTimeout(removeAttention, 6000);

  const handleFirstInteraction = () => {
    removeAttention();
    stopButton.removeEventListener('mouseenter', handleFirstInteraction);
    stopButton.removeEventListener('focus', handleFirstInteraction);
  };

  // Store the handler reference for cleanup
  (stopButton as any)._sirmaaiAttentionHandler = handleFirstInteraction;

  stopButton.addEventListener('mouseenter', handleFirstInteraction, { once: true });
  stopButton.addEventListener('focus', handleFirstInteraction, { once: true });

  logger.debug('Stop button event listeners setup complete with attention animation');
}

/**
 * Remove stop button event listeners for proper cleanup
 */
function removeStopButtonEventListeners(): void {
  if (!widgetInstance?.stopButton) {
    return;
  }

  const stopButton = widgetInstance.stopButton as any;

  logger.debug('Removing existing stop button event listeners');

  try {
    // Remove click listener
    if (stopButton._sirmaaiStopClickHandler) {
      stopButton.removeEventListener('click', stopButton._sirmaaiStopClickHandler);
      delete stopButton._sirmaaiStopClickHandler;
    }

    // Remove keydown listener
    if (stopButton._sirmaaiStopKeyDownHandler) {
      stopButton.removeEventListener('keydown', stopButton._sirmaaiStopKeyDownHandler);
      delete stopButton._sirmaaiStopKeyDownHandler;
    }

    // Clean up attention animation listeners
    if (stopButton._sirmaaiAttentionHandler) {
      stopButton.removeEventListener('mouseenter', stopButton._sirmaaiAttentionHandler);
      stopButton.removeEventListener('focus', stopButton._sirmaaiAttentionHandler);
      delete stopButton._sirmaaiAttentionHandler;
    }

    // Remove attention class if present
    stopButton.classList.remove('attention');

    // Clear visual styles that might have been applied
    stopButton.style.transform = '';
    stopButton.style.filter = '';
    stopButton.style.transition = '';

    // Clear the stored callback
    currentStopCallback = null;

    // Clean up screen reader live region
    const liveRegion = document.getElementById('sirmaai-live-region');
    if (liveRegion) {
      liveRegion.remove();
      logger.debug('Screen reader live region removed');
    }

    logger.debug('Stop button event listeners and accessibility features cleaned up successfully');
  } catch (error) {
    logger.warn('Error removing stop button event listeners', error);
  }
}

/**
 * Creates and shows the widget in the DOM with connecting state
 *
 * @param onStopCallback - Callback function when stop button is clicked
 */
export function showWidget(): void {
  logger.info('showWidget called', { hasExistingWidget: !!widgetInstance });

  // Remove existing widget if present
  if (widgetInstance) {
    logger.warn('Widget already exists, skipping creation');
    return;
  }

  const startTime = Date.now();

  try {
    logger.debug('Checking SVG support');
    if (!checkSvgSupport()) {
      throw new Error('SVG is not supported in this browser');
    }

    logger.debug('Getting translations');
    const translations = getTranslations();

    logger.debug('Getting widget position');
    const position = getWidgetPosition();

    logger.debug('Ensuring animation styles');
    ensureAnimationStyles();

    logger.debug('Creating widget elements');
    const elements = createWidgetElements(translations);
    widgetInstance = elements;

    logger.debug('Adding CSS classes for styling protection');
    // Add CSS classes for styling protection
    elements.widget.classList.add('sirmaai-widget');
    elements.widget.classList.add(`position-${position}`);
    elements.header.classList.add('sirmaai-header');
    elements.content.classList.add('sirmaai-content');
    elements.message.classList.add('sirmaai-message');
    elements.icon.classList.add('sirmaai-icon');
    elements.widget.setAttribute('role', 'status');

    logger.debug('Setting initial header text');
    // Set initial header text
    elements.headerTitle.textContent = translations.assistantName;

    logger.debug('Adding widget to DOM');
    // Add widget to DOM
    document.body.appendChild(elements.widget);

    logger.debug('Setting initial widget state to connecting');
    // Set initial connecting state
    updateWidget('connecting');

    const creationTime = Date.now() - startTime;
    logger.info('Widget displayed successfully', {
      creationTime: `${creationTime}ms`,
      assistantName: translations.assistantName,
    });
  } catch (error) {
    const creationTime = Date.now() - startTime;
    logger.error('Failed to show widget', {
      error: error instanceof Error ? error.message : error,
      stack: error instanceof Error ? error.stack : undefined,
      creationTime: `${creationTime}ms`,
    });
  }
}

/**
 * Updates the widget state with typed interface
 *
 * @param state - The new widget state
 * @param onStopCallback - Callback function for stop button (required for streaming state)
 */
export function updateWidget(
  state: WidgetState,
  {
    onStopCallback,
  }: {
    onStopCallback?: () => void;
  } = {}
): void {
  logger.info('updateWidget called', {
    state,
    hasWidgetInstance: !!widgetInstance,
    hasOnStopCallback: !!onStopCallback,
  });

  if (!widgetInstance) {
    logger.warn('Attempted to update widget but none exists', { state });
    return;
  }

  const startTime = Date.now();

  try {
    logger.debug('Getting translations for widget update');
    const translations = getTranslations();
    const { content, indicator, message, icon } = widgetInstance;

    logger.debug('Clearing previous widget classes and elements');
    // Clear previous classes
    indicator.className = '';
    icon.className = '';

    // Remove existing stop button
    const existingButton = content.querySelector('.sirmaai-stop-button');
    if (existingButton) {
      logger.debug('Removing existing stop button');
      existingButton.remove();
    }

    logger.debug('Updating widget icon and content');
    // Update icon and state
    icon.innerHTML = createStateIcon(state);

    const stateMessage = {
      connecting: translations.connecting,
      streaming: translations.streaming,
      error: translations.error,
      ended: translations.ended,
    }[state];

    // Set data attribute for state tracking (used for stop button visibility)
    widgetInstance.widget.setAttribute('data-sirmaai-state', state);

    // Update message text
    message.textContent = stateMessage;

    // Add SVG state visualization
    if (widgetInstance.svgElement) {
      logger.debug('Adding SVG state visualization', { state });
      addStateVisualization(widgetInstance.svgElement, state, translations);
    } else {
      logger.warn('No SVG element found for state visualization');
    }

    logger.debug('Applying state-specific updates', { state });
    // Update based on state
    switch (state) {
      case 'connecting':
        logger.debug('Setting connecting state indicators');
        indicator.classList.add('sirmaai-indicator', 'connecting');
        break;

      case 'streaming':
        logger.debug('Setting streaming state indicators');
        indicator.classList.add('sirmaai-indicator', 'streaming');

        // Setup enhanced stop button event handling if provided
        if (onStopCallback && widgetInstance.stopButton) {
          logger.debug('Setting up enhanced SVG stop button event handling');
          setupStopButtonEventListener(widgetInstance.stopButton, onStopCallback);

          // Focus management for accessibility
          requestAnimationFrame(() => {
            logger.debug('Focusing stop button for accessibility');
            const stopButton = widgetInstance?.stopButton;
            if (stopButton) {
              stopButton.focus();
            }
          });
        } else if (onStopCallback && !widgetInstance.stopButton) {
          logger.warn('Stop callback provided but no SVG stop button found');
        } else {
          // Remove existing event listeners if no callback provided
          logger.debug('Streaming state without stop callback, cleaning up existing listeners');
          removeStopButtonEventListeners();
        }
        break;

      case 'error':
        logger.debug('Setting error state indicators');
        indicator.classList.add('sirmaai-indicator', 'error');
        break;

      case 'ended':
        logger.debug('Setting ended state indicators');
        indicator.classList.add('sirmaai-indicator', 'ended');
        break;
    }

    const updateTime = Date.now() - startTime;
    logger.info('Widget state updated successfully', {
      state,
      stateMessage,
      updateTime: `${updateTime}ms`,
      hasStopButton: !!widgetInstance.stopButton,
    });
  } catch (error) {
    const updateTime = Date.now() - startTime;
    logger.error('Failed to update widget', {
      error: error instanceof Error ? error.message : error,
      stack: error instanceof Error ? error.stack : undefined,
      state,
      updateTime: `${updateTime}ms`,
    });
  }
}

/**
 * Hides and removes the widget from DOM after specified delay
 *
 * @param delay - Delay in milliseconds before hiding (0 for immediate)
 */
export function hideWidget({
  onRemove,
}: {
  onRemove?: () => void;
} = {}): void {
  logger.info('hideWidget called', {
    hasWidgetInstance: !!widgetInstance,
    hasOnRemove: !!onRemove,
  });

  if (!widgetInstance) {
    logger.warn('Attempted to hide widget but none exists');
    return;
  }

  logger.debug('Starting widget hide animation');
  if (widgetInstance?.widget && widgetInstance.widget.parentNode) {
    widgetInstance.widget.classList.add('hiding');

    logger.debug('Widget hiding, scheduling removal in 300ms');
    setTimeout(() => {
      if (widgetInstance?.widget && widgetInstance.widget.parentNode) {
        logger.debug('Removing widget from DOM');
        widgetInstance.widget.parentNode.removeChild(widgetInstance.widget);
      } else {
        logger.debug('Widget already removed from DOM');
      }

      const hadStopButton = !!widgetInstance?.stopButton;

      // Clean up stop button event listeners
      removeStopButtonEventListeners();

      widgetInstance = null;

      // Clean up audio visualizer if active
      removeStateVisualization(document.body);
      stopAudioVisualizer();

      logger.info('Widget hidden and instance cleared', {
        hadStopButton,
      });

      if (onRemove) {
        logger.debug('Calling onRemove callback');
        onRemove();
      }
    }, 300);
  } else {
    logger.warn('Widget instance or parent node not found during hide');
    widgetInstance = null;
  }
}

/**
 * Reset widget instance for testing purposes
 * @internal
 */
export function _resetState(): void {
  if (widgetInstance?.widget && widgetInstance.widget.parentNode) {
    widgetInstance.widget.parentNode.removeChild(widgetInstance.widget);
  }
  widgetInstance = null;
  currentStopCallback = null;
}

/**
 * Get current widget instance for testing purposes
 * @internal
 */
export function _getWidgetInstance(): WidgetElements | null {
  return widgetInstance;
}
